import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Zap } from "lucide-react";
import { api, extractApiError } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useI18n } from "@/contexts/I18nContext";

export default function Lobby() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [mode, setMode] = useState("public");
  const [language, setLanguage] = useState("fr");
  const [botDifficulty, setBotDifficulty] = useState("medium");
  const [openGames, setOpenGames] = useState([]);
  const [myWaiting, setMyWaiting] = useState([]);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchGames = async () => {
    try {
      const [open, mine] = await Promise.all([
        api.get("/games/open"),
        api.get("/games", { params: { mine: true, status: "waiting", limit: 5 } }),
      ]);
      setOpenGames(open.data);
      setMyWaiting(mine.data);
    } catch (_) {}
  };
  useEffect(() => {
    fetchGames();
    const int = setInterval(fetchGames, 5000);
    return () => clearInterval(int);
  }, []);

  const createGame = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/games", { mode, language });
      navigate(`/game/${data.game_id}`);
    } catch (e) {
      toast.error(extractApiError(e));
    } finally { setLoading(false); }
  };

  const startBotGame = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/games/bot", { difficulty: botDifficulty, language });
      navigate(`/game/${data.game_id}`);
    } catch (e) {
      toast.error(extractApiError(e));
    } finally { setLoading(false); }
  };

  const joinGame = async (gameId) => {
    try {
      await api.post(`/games/${gameId}/join`);
      navigate(`/game/${gameId}`);
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  const joinByCode = async () => {
    if (!code) return;
    try {
      const { data } = await api.post("/games/join-by-code", null, { params: { code } });
      navigate(`/game/${data.game_id}`);
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  return (
    <AppLayout>
      <div className="mb-8">
        <h1 className="font-display font-extrabold tracking-tight text-4xl">{t("lobby.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("lobby.subtitle")}</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="border-primary/40 bg-primary/5 lg:col-span-3" data-testid="lobby-bot-card">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2">
              <Bot className="w-5 h-5 text-primary" /> {t("lobby.bot.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <Label>{t("lobby.bot.difficulty")}</Label>
              <Select value={botDifficulty} onValueChange={setBotDifficulty}>
                <SelectTrigger className="h-11 mt-1" data-testid="lobby-bot-diff"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">{t("lobby.bot.easy")}</SelectItem>
                  <SelectItem value="medium">{t("lobby.bot.medium")}</SelectItem>
                  <SelectItem value="hard">{t("lobby.bot.hard")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px]">
              <Label>{t("lobby.create.language")}</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button size="lg" onClick={startBotGame} disabled={loading} data-testid="lobby-bot-start-btn">
              <Zap className="w-4 h-4 mr-1" /> {t("lobby.bot.start")}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader><CardTitle className="font-display">{t("lobby.create.title")}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="h-11 mt-1" data-testid="lobby-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">{t("lobby.create.public")}</SelectItem>
                  <SelectItem value="private">{t("lobby.create.private")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("lobby.create.language")}</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger className="h-11 mt-1" data-testid="lobby-lang-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button size="lg" className="w-full" onClick={createGame} disabled={loading} data-testid="lobby-create-btn">
              {t("lobby.create.button")}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-border lg:col-span-2">
          <CardHeader><CardTitle className="font-display">{t("lobby.open")}</CardTitle></CardHeader>
          <CardContent>
            {openGames.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">—</div>
            ) : (
              <div className="divide-y divide-border">
                {openGames.map((g) => (
                  <div key={g.game_id} className="py-3 flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={g.host?.picture} />
                      <AvatarFallback>{(g.host?.name || "?").slice(0,2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{g.host?.name}</div>
                      <div className="text-xs text-muted-foreground">{g.language.toUpperCase()} · {new Date(g.created_at).toLocaleTimeString()}</div>
                    </div>
                    <Button size="sm" onClick={() => joinGame(g.game_id)} data-testid={`lobby-join-${g.game_id}`}>{t("lobby.join")}</Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border lg:col-span-2">
          <CardHeader><CardTitle className="font-display">{t("lobby.joinCode")}</CardTitle></CardHeader>
          <CardContent className="flex gap-2">
            <Input placeholder="XXXXXX" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="h-11 uppercase tracking-widest font-mono" maxLength={6} data-testid="lobby-code-input" />
            <Button className="h-11" onClick={joinByCode} data-testid="lobby-code-join-btn">{t("lobby.joinCode.button")}</Button>
          </CardContent>
        </Card>

        {myWaiting.length > 0 && (
          <Card className="border-border">
            <CardHeader><CardTitle className="font-display text-base">Vos parties en attente</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {myWaiting.map((g) => (
                <div key={g.game_id} className="flex items-center justify-between text-sm">
                  <div>
                    <div>{g.mode === "private" ? "Privée" : "Publique"}</div>
                    {g.invite_code && <Badge variant="secondary" className="mt-1 font-mono">{g.invite_code}</Badge>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/game/${g.game_id}`)}
                    data-testid={`lobby-continue-${g.game_id}`}>Ouvrir</Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
